import { Button, cn, Input, Label } from '@up2stay/up2promo-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import ButtonWithEnter from '../components/button-with-enter';
import { SetupStepShell } from '../components/setup-step-shell';
import { useIntercom } from '@up2promo-setup-frontend/core/hooks/use-intercom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { motion } from 'motion/react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSetupNavigation } from './hooks/use-setup-navigation';

export default function EmailPage() {
  const translations = useDictionary();
  const { goNext } = useSetupNavigation();

  const email = useSetupStore((state) => state.email);
  const hash = useSetupStore((state) => state.hash);
  const type = useSetupStore((state) => state.type);
  const setEmail = useSetupStore((state) => state.setEmail);

  const intercom = useIntercom({
    user: hash ? { hash } : undefined,
    enabled: true,
  });

  const emailSchema = z.object({
    email: z.string().email(translations.pages.emailStep.invalidEmail),
  });
  type EmailInfoValues = z.infer<typeof emailSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailInfoValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    // Zod 4 + current @hookform/resolvers typings are mismatched in this workspace.
    resolver: zodResolver(emailSchema as never),
    defaultValues: {
      email: email || '',
    },
  });

  const onSubmit = (data: EmailInfoValues) => {
    const trimmedEmail = data.email.trim();
    setEmail(trimmedEmail);

    if (intercom.isBooted()) {
      intercom.trackEvent('email-submitted', {
        hash,
        email_provided: true,
        email_domain: trimmedEmail.includes('@')
          ? trimmedEmail.split('@')[1]
          : undefined,
        current_type: type,
      });
    }

    goNext('email');
  };

  const handleSkip = useCallback(() => {
    setEmail(undefined);

    if (intercom.isBooted()) {
      intercom.trackEvent('email-skipped', {
        hash,
        email_provided: false,
        current_type: type,
      });
    }

    goNext('email');
  }, [setEmail, goNext, intercom, hash, type]);

  return (
    <SetupStepShell>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-2 sm:gap-3"
      >
        <motion.h1
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
        >
          {translations.pages.emailStep.title}
        </motion.h1>
        <motion.p
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="max-w-prose text-sm text-foreground/70 text-pretty md:text-base"
        >
          {translations.pages.emailStep.description}
        </motion.p>

        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="my-4 flex flex-col gap-2 sm:my-6"
        >
          <Label htmlFor="setup-email">
            {translations.pages.emailStep.inputTitle}
          </Label>
          <div className="flex flex-col gap-1">
            <Input
              id="setup-email"
              autoFocus
              {...register('email')}
              placeholder={translations.pages.emailStep.inputPlaceholder}
              aria-invalid={!!errors.email}
              className={cn(errors.email && 'border-destructive')}
            />
            {errors.email?.message && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
        </motion.div>

        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <ButtonWithEnter
            type="submit"
            text={translations.pages.emailStep.button}
          />
        </motion.div>

        <motion.div
          custom={4}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mt-4 flex flex-col gap-2 sm:mt-6 sm:gap-3"
        >
          <div className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 sm:gap-3 sm:p-4">
            <Icon
              icon="mdi:information-outline"
              className="mt-0.5 shrink-0 text-foreground"
              width={18}
            />
            <p className="text-xs text-foreground/70 sm:text-sm">
              {translations.pages.emailStep.skipWarning}
            </p>
          </div>
          <Button type="button" variant="ghost" onClick={handleSkip}>
            {translations.pages.emailStep.skipButton}
          </Button>
        </motion.div>
      </form>
    </SetupStepShell>
  );
}
